import { useState, type ReactNode } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { CheckCircle2, FileSearch, Loader2, Search, Undo2 } from 'lucide-react';
import { useLocalization } from '@/hooks/useLocalization';
import { useUserRole } from '@/hooks/useUserRole';
import { useProjectFacts, type DetectedFact, type FactKind, type ProjectFactGroup } from '@/hooks/useProjectFacts';

const REVIEW_ROLES = ['pmo', 'executive', 'tenant_admin', 'admin'];

interface Props<TApproved extends { id: string; project_id: string }> {
  kind: FactKind;
  title: string;
  description: string;
  /** Noun for button and messages, plural: "stakeholders". */
  noun: string;
  /** One detected (not yet approved) fact. */
  renderDetected: (fact: DetectedFact) => ReactNode;
  /** One approved row. */
  renderApproved: (row: TApproved) => ReactNode;
  /** Text a fact or row is searched by. */
  searchText: (row: TApproved | DetectedFact) => string;
}

/**
 * Per-project review of facts read from project documents, shown inside the tab that owns them.
 * Each project has what was detected (all ticked; untick to skip; one button adds the rest)
 * and what is already added, with an undo for the project.
 */
export function ProjectFactsSection<TApproved extends { id: string; project_id: string }>(props: Props<TApproved>) {
  const { kind, title, description, noun, renderDetected, renderApproved, searchText } = props;
  const { t } = useLocalization();
  const { role, loading: roleLoading } = useUserRole();
  const canManage = !roleLoading && REVIEW_ROLES.includes(role ?? '');
  const { groups, totals, loading, busyProject, error, approve, undo, approveAll, undoAll } = useProjectFacts<TApproved>(kind);
  const [unticked, setUnticked] = useState<Set<string>>(new Set());
  const [confirmUndo, setConfirmUndo] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [confirmUndoAll, setConfirmUndoAll] = useState(false);

  if (loading) return null;
  if (groups.length === 0) return null;

  const q = query.trim().toLowerCase();
  const visible = groups.filter((g) =>
    !q || g.projectName.toLowerCase().includes(q) || g.detected.some((d) => searchText(d).toLowerCase().includes(q)) || g.approved.some((a) => searchText(a).toLowerCase().includes(q)));
  // Projects with something to review first, then by name.
  const ordered = [...visible].sort((a, b) => Number(b.detected.length > 0) - Number(a.detected.length > 0) || a.projectName.localeCompare(b.projectName));

  const toggle = (id: string) => setUnticked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const onApprove = async (g: ProjectFactGroup<TApproved>) => {
    setNotice(null);
    const excluded = g.detected.filter((d) => unticked.has(d.id)).map((d) => d.id);
    const res = await approve(g, excluded);
    if (res) setNotice(`${g.projectName}: ${res.approved} ${noun} added${res.rejected ? `, ${res.rejected} skipped` : ''}.`);
  };

  const onUndo = async (g: ProjectFactGroup<TApproved>) => {
    if (confirmUndo !== g.projectId) { setConfirmUndo(g.projectId); return; }
    setConfirmUndo(null);
    setNotice(null);
    const res = await undo(g);
    if (res) setNotice(`${g.projectName}: ${res.reverted} moved back to review${res.skipped.length ? `; ${res.skipped.length} edited since were kept` : ''}.`);
  };

  const allBusy = busyProject === '*';
  const onApproveAll = async () => {
    setNotice(null);
    const res = await approveAll();
    if (res) setNotice(`${res.approved} ${noun} ${t('added across')} ${res.projects} ${t('projects')}.`);
  };
  const onUndoAll = async () => {
    if (!confirmUndoAll) { setConfirmUndoAll(true); return; }
    setConfirmUndoAll(false);
    setNotice(null);
    const res = await undoAll();
    if (res) setNotice(`${res.reverted} ${t('moved back to review')}${res.skipped ? `; ${res.skipped} ${t('edited since were kept')}` : ''}.`);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileSearch className="h-4 w-4 text-primary" aria-hidden="true" />
              {t(title)}
              {totals.detected > 0 && <Badge variant="secondary">{totals.detected} {t('to review')}</Badge>}
            </CardTitle>
            <CardDescription className="mt-1">{t(description)}</CardDescription>
          </div>
          {canManage && (totals.detected > 0 ? (
            <Button onClick={onApproveAll} disabled={allBusy || busyProject !== null}>
              {allBusy ? <Loader2 className="me-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="me-1.5 h-4 w-4" />}
              {t('Add all')} {totals.detected} {noun}
            </Button>
          ) : totals.approved > 0 ? (
            <Button variant="ghost" size="sm" onClick={onUndoAll} disabled={busyProject !== null} className="text-muted-foreground">
              <Undo2 className="me-1.5 h-3.5 w-3.5" />
              {confirmUndoAll ? t('Click again to undo all') : t('Undo all')}
            </Button>
          ) : null)}
          {groups.length > 6 && (
            <div className="relative w-full sm:w-64">
              <Search className="pointer-events-none absolute start-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('Filter by project or text')} className="ps-8" aria-label={t('Filter by project or text')} />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        {notice && <p role="status" className="text-sm text-muted-foreground">{notice}</p>}
        {ordered.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">{t('Nothing matches that filter.')}</p>}
        <Accordion type="multiple" className="w-full">
          {ordered.map((g) => {
            const toAdd = g.detected.filter((d) => !unticked.has(d.id)).length;
            const busy = busyProject === g.projectId;
            return (
              <AccordionItem key={g.projectId} value={g.projectId}>
                <AccordionTrigger className="py-3 text-start hover:no-underline">
                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="truncate text-sm font-medium">{g.projectName}</span>
                    {g.detected.length > 0 && <Badge variant="secondary">{g.detected.length} {t('to review')}</Badge>}
                    {g.approved.length > 0 && <Badge variant="outline">{g.approved.length} {t('added')}</Badge>}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-4">
                  {g.detected.length > 0 && (
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h4 className="text-sm font-semibold">{t('Detected from documents')}</h4>
                        {canManage && (
                          <Button size="sm" onClick={() => onApprove(g)} disabled={busy || toAdd === 0}>
                            {busy ? <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="me-1.5 h-3.5 w-3.5" />}
                            {t('Add')} {toAdd} {noun}
                          </Button>
                        )}
                      </div>
                      <ul className="mt-2 space-y-2">
                        {g.detected.map((d) => (
                          <li key={d.id} className="flex items-start gap-3 rounded-md border bg-background p-3">
                            <Checkbox checked={!unticked.has(d.id)} onCheckedChange={() => toggle(d.id)} disabled={busy || !canManage} className="mt-0.5" aria-label={searchText(d)} />
                            <div className="min-w-0 flex-1">{renderDetected(d)}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {g.approved.length > 0 && (
                    <div>
                      <ul className="space-y-2">
                        {g.approved.map((a) => (
                          <li key={a.id} className="rounded-md border p-3">{renderApproved(a)}</li>
                        ))}
                      </ul>
                      {canManage && g.dossierKey && (
                        <div className="mt-2 flex justify-end">
                          <Button variant="ghost" size="sm" onClick={() => onUndo(g)} disabled={busy} className="text-muted-foreground">
                            <Undo2 className="me-1.5 h-3.5 w-3.5" />
                            {confirmUndo === g.projectId ? t('Click again to move them back to review') : t('Undo import')}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </CardContent>
    </Card>
  );
}

export default ProjectFactsSection;
